export const isChatVideoName = (name) => /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(String(name || ''))
export const isChatImageName = (name) => /\.(jpe?g|png|gif|webp|heic|heif)(\?|$)/i.test(String(name || ''))

export const encodeChatFileToken = (stored, original) => {
  const label = String(original || stored || 'file')
    .replace(/\|/g, '_')
    .replace(/[\u0000-\u001f]/g, '')
    .trim()
    .slice(0, 120) || 'file'
  return `file:${stored}|${label}`
}

export const parseChatMedia = (text) => {
  const raw = String(text || '')
  if (raw.startsWith('voice:')) {
    const file = raw.slice(6)
    return { kind: 'voice', file, src: mediaFileUrl(file) }
  }
  if (raw.startsWith('video:')) {
    const file = raw.slice(6)
    return { kind: 'video', file, src: mediaFileUrl(file) }
  }
  if (raw.startsWith('photo:')) {
    const file = raw.slice(6)
    return { kind: 'image', file, src: mediaFileUrl(file) }
  }
  if (raw.startsWith('clip:')) {
    const file = raw.slice(5)
    if (isChatVideoName(file)) return { kind: 'fileVideo', file, src: mediaFileUrl(file) }
    return { kind: 'image', file, src: mediaFileUrl(file) }
  }
  if (raw.startsWith('file:')) {
    const rest = raw.slice(5)
    const sep = rest.indexOf('|')
    const file = sep >= 0 ? rest.slice(0, sep) : rest
    const label = sep >= 0 ? rest.slice(sep + 1) : file
    return { kind: 'file', file, label, src: mediaFileUrl(file) }
  }
  if (/^https?:\/\//i.test(raw)) return { kind: 'image', src: raw }
  return { kind: 'text', src: raw }
}

export const mediaFileUrl = (file) => {
  const name = String(file || '').replace(/^\/?api\/uploads\//, '')
  const origin = (typeof window !== 'undefined' && window.location.port === '3000')
    ? `${window.location.protocol}//${window.location.hostname}:3001`
    : ''
  return `${origin}/api/uploads/${encodeURIComponent(name)}`
}

export const mediaPreviewLabel = (text) => {
  const kind = parseChatMedia(text).kind
  if (kind === 'voice') return 'Voice message'
  if (kind === 'video' || kind === 'fileVideo') return 'Video message'
  if (kind === 'file') return parseChatMedia(text).label || 'File'
  if (kind === 'image' && /\.gif(\?|$)/i.test(String(text || ''))) return 'GIF'
  if (kind === 'image') return 'Photo'
  return text
}

export const formatDuration = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}

export const pickRecorderMime = (kind) => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return ''
  const video = ['video/mp4', 'video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm']
  const audio = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm']
  return (kind === 'video' ? video : audio).find(type => MediaRecorder.isTypeSupported(type)) || ''
}

export const openChatStream = async (kind) => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new Error('Recording is not available on this device')
  }
  if (kind !== 'video') return navigator.mediaDevices.getUserMedia({ audio: true })
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 480 } }
    })
  } catch (_) {
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
    } catch (__) {
      return navigator.mediaDevices.getUserMedia({ video: true })
    }
  }
}

export const createChatRecorder = (stream, kind) => {
  if (typeof MediaRecorder === 'undefined') throw new Error('This browser cannot record video')
  const mime = pickRecorderMime(kind)
  const withBitrate = mime
    ? { mimeType: mime, videoBitsPerSecond: 700000, audioBitsPerSecond: 64000 }
    : { videoBitsPerSecond: 700000, audioBitsPerSecond: 64000 }
  try {
    return new MediaRecorder(stream, kind === 'video' ? withBitrate : (mime ? { mimeType: mime } : {}))
  } catch (_) {
    try {
      return mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream)
    } catch (err) {
      return new MediaRecorder(stream)
    }
  }
}

export const startChatRecorder = (recorder) => {
  try {
    recorder.start(250)
  } catch (_) {
    recorder.start()
  }
}

export const chatFileName = (kind, mime) => {
  const ext = /mp4/i.test(mime) ? 'mp4' : /ogg/i.test(mime) ? 'ogg' : 'webm'
  return `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`
}

export const uploadChatBlob = async (blob, kind) => {
  const mime = blob.type || (kind === 'video' ? 'video/webm' : 'audio/webm')
  const name = chatFileName(kind, mime)
  const file = new File([blob], name, { type: mime })
  return uploadChatFile(file)
}

export const uploadChatFile = async (file) => {
  const formData = new FormData()
  formData.append('myfile', file)
  const response = await fetch('/api/up/chat', { method: 'POST', body: formData })
  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data.name) {
    if (response.status === 413) throw new Error('File is too large. Choose a smaller file.')
    throw new Error(data.msg || 'Could not upload file')
  }
  return data.name
}

export const waveBars = (seed, count = 22) => {
  const text = String(seed || 'wave')
  let hash = 0
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0
  return Array.from({ length: count }, (_, index) => 28 + ((hash >> (index % 12)) + index * 17) % 72)
}
